<template>
  <div class="app" :class="{ 'hide-cursor': isTerminalChannel && !terminalMouseCursorEnabled }" :style="getBgImage">
    <TicketHistory v-if="!isTerminalChannel" />
    <Modal
      v-if="ticketCanceled"
      ref="modal"
      show-footer
      class="cancel-ticket-confirm-modal"
      html-id="cancel-confirm-modal"
      @close-modal="closeCancelTicketModal"
      @close-modal-outside="closeCancelTicketModal('outside')"
    >
      <template #header>
        <h3>
          {{ translations.general_info }}
        </h3>
      </template>
      <template #body>
        <div>
          {{ ticketCanceledMessage }}
          <div class="modal-actions">
            <button class="button" @click="closeCancelTicketModal">
              {{ translations.general_ok }}
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <div>
          <button class="button" @click="closeCancelTicketModal">
            {{ translations.general_ok }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetModal"
      ref="modal"
      class="free-bet-modal"
      :class="[{ mobile: isMob }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="claimFreeBets()"
      @close-modal-outside="claimFreeBets('outside')"
    >
      <template #body>
        <div class="body">
          <img src="./assets/free-bet-star.png" alt="" />
          <div class="body-message-box">
            <div class="pre-title">{{ translations.general_free_bet_pre_title.toUpperCase() }}</div>
            <div class="title">{{ translations.general_free_bet }}</div>
            <div class="subtitle">{{ translations.general_congratulation.toUpperCase() }}</div>
            <span class="subtitle-2">{{ translations.general_claim_ticket }}</span>
          </div>
          <div class="action">
            <button class="button" @click="claimFreeBets">
              {{ claimFreeBetsLabel }}
            </button>
          </div>
        </div>
      </template>
    </Modal>
    <Modal
      v-if="showFreeBetExpiredModal"
      ref="modal"
      class="free-bet-expired-modal"
      :class="[{ mobile: isMobile }, { desktop: isDesktop }]"
      width="344px"
      height="348px"
      show-footer
      @close-modal="toggleFreeBetExpiredModal(false)"
      @close-modal-outside="toggleFreeBetExpiredModal('outside')"
    >
      <template #header>
        <h3>
          {{ translations.general_info }}
        </h3>
      </template>
      <template #body>
        <div>
          {{ freeBetExpiredMessage }}
        </div>
      </template>
      <template #footer>
        <div>
          <button class="button" @click="toggleFreeBetExpiredModal(false)">
            {{ translations.general_ok }}
          </button>
        </div>
      </template>
    </Modal>
    <LandscapeNotification />
    <router-view></router-view>
  </div>
</template>

<script>
import applyPalette from '@nsftx/games-sdk-js/src/utility/applyPalette';
import eventBus from '@nsftx/games-sdk-js/src/utility/eventBus';
import gtag from '@nsftx/games-sdk-js/src/utility/gtag';
import { mapState, mapGetters, mapActions } from 'vuex';
import { BusService } from '@nsftx/games-bus';
import { has, merge, isArray, startCase, assign, toUpper, isEmpty, lowerFirst, cloneDeep, toLower } from 'lodash';
import isMobile from 'ismobilejs';
import NoSleep from 'nosleep.js';
import Modal from '@nsftx/games-sdk-js/src/components/Modal';
import webViewIntegration from '@/utility/webViewIntegration';
import { ulid } from 'ulid';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'App',
  components: {
    TicketHistory: defineAsyncComponent(
      () => import(/* webpackChunkName: "TicketHistory" */ '@/components/TicketHistory'),
    ),
    Modal,
    LandscapeNotification: defineAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "LandscapeNotification" */ '@nsftx/games-sdk-js/src/components/LandscapeNotification'
        ),
    ),
  },
  data() {
    return {
      busService: null,
      isLoggedIn: false,
      check: null,
      checkTimeout: 10000,
      ticketCanceled: false,
      canceledTicket: {},
      lastTicketsCalled: false,
      userFreeBets: {
        count: 0,
        betStake: 0,
      },
    };
  },
  computed: {
    ...mapState([
      'config',
      'offer',
      'currentRound',
      'expiredBets',
      'disabledRoundBySocket',
      'userUuid',
      'isMobile',
      'jackpotState',
      'isSetStateTransfered',
      'terminalUserData',
      'ticketDetailsData',
      'activeTerminalLayout',
      'terminalMouseCursorEnabled',
      'showFreeBetModal',
      'showFreeBetExpiredModal',
      'nextgenStandaloneIntegration',
      'printedTicketsByTicketCheck',
    ]),
    ...mapGetters([
      'user/user',
      'translations',
      'isIsonis',
      'userIsOperator',
      'ajsDisabled',
      'isDesktop',
      'thirdPartyInfo',
      'isNextGen',
      'customDomain',
      'isTerminalChannel',
      'isThirdPartyIntegration',
    ]),
    ...mapGetters('gamesBetslip', ['ticket']),
    ...mapGetters('notifications', ['notificationTypes']),
    ...mapGetters({
      isUserLoggedIn: 'user/isLoggedIn',
    }),
    isMob() {
      return this.isMobile || window.innerWidth <= 660;
    },
    userProfile() {
      return this['user/user'];
    },
    getBgImage() {
      if (
        this.activeTerminalLayout === 'TicketCheck' ||
        this.activeTerminalLayout === 'TicketHistory' ||
        this.isMobile
      ) {
        return {
          backgroundColor: 'var(--card)',
          backgroundImage: 'none',
        };
      }
      return this.config.ui.dark ? this.bgImage : this.bgImageLight;
    },
    bgImage() {
      return {
        backgroundImage: `linear-gradient(to bottom, rgba(26, 28, 29, 0), #1A1C1D 88%), url(${require('@/assets/' + this.config.productName + '_dark_bg.jpg')})`,
      };
    },
    bgImageLight() {
      return {
        backgroundImage: `linear-gradient(to bottom, rgba(26, 28, 29, 0), #FFFFFF 88%), url(${require('@/assets/' + this.config.productName + '_light_bg.jpg')})`,
      };
    },
    isAndroidPlatform() {
      return webViewIntegration.isActive() && has(window, 'WebAppListener');
    },
    getProductName() {
      return this.config.productName.split(/(?=[A-Z])/).join(' ');
    },
    ticketCanceledMessage() {
      return `${this.translations.general_ticket} ${this.canceledTicket.id} ${this.translations.general_successfully_cancelled}!`;
    },
    getTicketDetailsAuth() {
      return btoa(`${this.config.user.id}:${this.terminalUserData.auth.password}`);
    },
    getTicketHistoryAuthHeaders() {
      const headers = {
        Authorization: `Bearer ${this.terminalUserData.auth.token}`,
        'HTTP-X-NAB-BETSHOP-ID': this.config.betshop.id,
        'HTTP-X-NAB-COMPANY-ID': this.config.companyData.id,
        'HTTP-X-NAB-DEVICE-UUID': this.config.device.deviceUuid,
        'HTTP-X-NAB-DP': 'AdminTerminal',
        'SEVEN-LOCALE': this.config.locale,
      };
      return headers;
    },
    productNameLabel() {
      return this.translations[this.config.productNameLabel];
    },
    isStandAlone() {
      return this.config.ui.config.mode === 'standalone';
    },
    isGravityGateway() {
      return this.config.messageProtocol.name === 'gravityGateway';
    },
    claimFreeBetsLabel() {
      if (this.isMob) {
        return this.translations.general_claim;
      }
      return this.translations.general_ok;
    },
    freeBetExpiredMessage() {
      return this.translations.general_free_bet_expired_message;
    },
    userLogged() {
      return this.userProfile.profile.logged || this.isLoggedIn;
    },
  },
  beforeMount() {
    let subChannels = {};
    if (this.isTerminal) {
      subChannels = {
        Product: {
          deviceUuid: this.config.device.uuid,
        },
      };
      if (!isEmpty(this.thirdPartyInfo)) {
        const { filterMessagesBy } = this.thirdPartyInfo;
        if (filterMessagesBy && filterMessagesBy.length) {
          filterMessagesBy.forEach((filter) => {
            subChannels.Product[lowerFirst(filter.name)] = filter.value;
          });
        }
      }
    }

    this.busService = new BusService({
      environment: this.config.environment,
      platform: this.config.platform,
      productName: this.config.productName,
      productId: this.config.productId,
      tenantId: this.config.tenantId,
      locale: this.config.locale,
      applicationName: this.config.applicationName,
      subChannels,
      useCommonEventName: true,
      adapters: [{ name: 'GcmAdapter' }],
      clientMessageProtocol: this.config.messageProtocol,
      bounceToFrames: [{ frameId: 'racersVisualization' }],
      customDomain: this.customDomain,
    });
    const device = this.isMob ? 'mobile' : 'desktop';
    this.busService.start();
    this.setGtag(device);
    this.setIsMobile(isMobile().any && window.innerWidth <= 640);
    (async () => {
      if (!this.ajsDisabled) {
        await this.setJackpotState();
        if (this.isTerminalChannel) {
          this.busService.addChannel('Device', this.config.device.uuid);
        }
        if (this.jackpotState) {
          this.busService.addChannel('Jackpot', this.jackpotState.jackpotId);
        }
      }
      if (this.isNextGen && this.userProfile?.profile?.id) {
        this.busService.addChannel('Player', this.userProfile.profile.id);
      }
    })();
  },
  mounted() {
    if (this.isMobile) {
      const noSleep = new NoSleep();
      let wakeLockEnabled = false;
      document.addEventListener(
        'click',
        function enableNoSleep() {
          document.removeEventListener('click', enableNoSleep, false);
          if (!wakeLockEnabled) {
            noSleep.enable();
            wakeLockEnabled = true;
          } else {
            noSleep.disable();
            wakeLockEnabled = false;
          }
        },
        false,
      );
    }
    this.setPageTitle();
    this.setTheme(this.config);
    if (this.isAndroidPlatform) {
      window.WebAppListener.sendMessage('Slave.Loaded');
    }
    this.busService.sendMessage(
      {
        action: 'Slave.Loaded',
      },
      true,
    );
    const postMsgData = {
      messageType: 'Games:Init:Acknowledged',
    };
    window.top.postMessage(postMsgData, '*');
    window.addEventListener('Bus:Message', (event) => {
      const { eventName } = event.detail;
      switch (eventName) {
        case 'SetState':
          this.handleSetState(event.detail);
          break;
        case 'StartEvent':
          this.handleStartEvent(event.detail);
          break;
        case 'StopBetting':
          this.handleStopBetting(event.detail);
          break;
        case 'StartRace':
          this.handleStartRace(event.detail);
          break;
        case 'User.AuthorizationChanged':
          this.handleAuthorizationChanged(event.detail);
          break;
        case 'User.ProfileChanged':
          this.setUserBalance(event.detail.data.profile.balance);
          break;
        case 'User.SettingsChanged':
          if (!this.isLoggedIn) {
            this.resetUserFreeBets();
            this.clearBetslip();
            this.setUserFreeBets(this.userFreeBets);
          } else {
            this.getUserFreeBets();
          }
          break;
        case 'User.BalanceChanged':
          this.setUserBalance(event.detail.data.balance);
          break;
        case 'TicketUpdate': {
          if (this.isTerminalChannel) this.handleTerminalTicketUpdate(event.detail);
          else this.handleWebTicketUpdate(event.detail);
          break;
        }
        case 'Tickets.FetchHistory':
          this.handleTicketsFetchHistory();
          break;
        case 'Tickets.Checked':
          this.handleTicketsChecked(event.detail);
          break;
        case 'FreeBet':
        case 'freebet':
          this.userFreeBets = event.detail;
          // eslint-disable-next-line no-case-declarations
          const { subtype } = event.detail;
          if (subtype === 'new') {
            this.toggleFreeBetModal(true);
          } else {
            if (this.userProfile.freeBets.count > 0) {
              this.toggleFreeBetExpiredModal(true);
            }
            this.getUserFreeBets();
          }
          break;
        case 'UI.Show': {
          this.setActiveTerminalLayout(event.detail.data.name[0]);
          break;
        }
        // IGNITE
        case 'onSettingsUpdated':
          if (this.config.locale !== event.detail.data.activeLocale) {
            this.updateConfig({ locale: event.detail.data.activeLocale });
            // TODO HANDLE TRANSLATIONS CHANGE
          }
          if (this.config.currency !== event.detail.data.activeCurrencyBalance) {
            this.updateConfig({ currency: event.detail.data.activeCurrency.code });
          }
          break;
        case 'onBettingDenied':
          this.disableAllBetting(true);
          this.setIsPayinButtonDisabled(true);
          this.clearAndSetNotification({
            message: this.translations.betting_disabled,
            status: 'negative',
          });
          break;
        case 'onBettingAllowed':
          this.disableAllBetting(false);
          this.setIsPayinButtonDisabled(false);
          this.clearNotification();
          break;
        case 'onCancelBetsRequested':
          // eslint-disable-next-line no-console
          console.log('CANCEL BETS REQUESTED:', event.detail.data);
          this.cancelTicket({
            id: event.detail.data.betIds[0],
            providerBetslipId: event.detail.data.providerBetslipId,
          });
          break;
        case 'onReBetRequested':
          // eslint-disable-next-line no-console
          console.log('REBET REQUESTED:', event.detail.data);
          // eslint-disable-next-line no-console
          console.warn('Rebet not possible on this game');
          break;
        case 'Tickets.PayingSentSuccess':
        case 'Tickets.PayoutSentSuccess':
        case 'Tickets.CancelSentSuccess': {
          if (this.isThirdPartyIntegration) {
            const { ticket, action } = event.detail.data;
            const requestId = ticket?.requestId;
            if (requestId) {
              this.checkTicket(requestId, action);
            }
          }
          break;
        }
        case 'Tickets.PayingFailed':
        case 'Tickets.PayoutFailed':
        case 'Tickets.CancelFailed': {
          this.showTerminalNotification({
            message: event.detail.data.error,
            type: 'warning',
            delay: 3000,
          });
          this.setIsPayinButtonDisabled(false);
          this.setIsPayinInProgress(false);
          this.clearCheckTimeout();
          break;
        }
        default:
          break;
      }
    });
    eventBus.$on('RemoveInvalidBets', () => {
      this.setExpiredBets(false);
      this.clearNotification();
    });
    eventBus.$on('LoginRequired', () => {
      this.busService.sendMessage(
        {
          action: 'User.LoginRequired',
        },
        true,
      );
      if (this.isAndroidPlatform) {
        window.WebAppListener.sendMessage('User.LoginRequired');
      }
    });
    eventBus.$on('LoadLastTickets', () => {
      this.getLastTickets();
    });
    eventBus.$on('TicketPayin', this.isTerminalChannel ? this.ticketPayinTerminal : this.ticketPayin);
    eventBus.$on('BetRemoved', () => {
      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      window.dispatchEvent(clickEvent);
    });
    eventBus.$on('ReturnToLobby', () => {
      this.busService.sendMessage(
        {
          action: 'UI.Show',
          data: {
            name: 'Lobby',
          },
        },
        true,
      );
      if (this.isAndroidPlatform) {
        window.WebAppListener.sendMessage('Navigation.Back');
      }
    });
    eventBus.$on('PrintTicketCopy', (ticket) => {
      const mappedTicket = assign(ticket, { isCopy: true }, { translation: this.productNameLabel });
      const isFuture = this.checkIsTicketFuture(ticket);
      mappedTicket.isFuture = isFuture;

      mappedTicket.bets.forEach((bet) => {
        // eslint-disable-next-line no-param-reassign
        bet.possibleTax = bet.possibleTax || 0;
      });
      this.busService.sendMessage(
        {
          action: 'Peripherals.Print',
          enforceEvent: true,
          data: {
            type: 'ticket',
            productId: 'GreyhoundRaces',
            data: mappedTicket,
          },
        },
        true,
      );
    });
    eventBus.$on('PayoutTicket', (ticket) => {
      if (this.isThirdPartyIntegration) {
        this.showTerminalNotification({
          message: this.translations.general_ticket_resolving,
          type: 'info',
          delay: null,
        });
      } else {
        this.checkTicket(ticket.requestUuid, 'Payout');
      }
      this.busService.sendMessage(
        {
          action: 'Tickets.Payout',
          data: {
            ticket,
          },
        },
        true,
      );
    });
    eventBus.$on('TicketCancel', (ticket) => {
      if (this.isThirdPartyIntegration) {
        this.showTerminalNotification({
          message: this.translations.general_ticket_resolving,
          type: 'info',
          delay: null,
        });
      } else {
        this.checkTicket(ticket.requestUuid, 'Cancel');
      }
      this.busService.sendMessage(
        {
          action: 'Tickets.Cancel',
          data: {
            ticket,
          },
        },
        true,
      );
    });
    eventBus.$on('CloseTicketDetails', () => {
      this.setLoaded(false);
      this.setTicketDetailsData({});
      if (this.isThirdPartyIntegration) this.setActiveTerminalLayout('BettingArea');
      else {
        this.busService.sendMessage(
          {
            action: 'UI.Show',
            data: {
              name: 'BettingArea',
            },
          },
          true,
        );
      }
    });
    eventBus.$on('TicketCancelled', async (ticketId) => {
      this.ticketCanceled = true;
      this.canceledTicket = { id: ticketId };
      this.getLastTickets();
      setTimeout(async () => {
        if (this.nextgenStandaloneIntegration) {
          await window.igniteInstance.getWallets().then((result) => {
            if (result.activeCurrencyBalance) {
              this.setUserBalance(result.activeCurrencyBalance);
            }
          });
        }
      }, 1000);
    });
    if (this.config.platformConfig.clientType) {
      if (this.userProfile && this.userProfile?.profile?.id) {
        this.togglePlayerChannel(this.userProfile.profile.id);
        this.getLastTickets();
      }
    }
  },
  methods: {
    ...mapActions([
      'setState',
      'stopBetting',
      'setOffer',
      'parseOdds',
      'setUserBalance',
      'updateTicketValues',
      'setExpiredBets',
      'setDisabledRoundBySocket',
      'setUserUuid',
      'setIsMobile',
      'setJackpotState',
      'setStateMessage',
      'setNextRoundTime',
      'setInProgressRound',
      'setCurrentScheduleRound',
      'setTicketHistoryTerminal',
      'setTicketDetailsData',
      'setLoaded',
      'updateConfig',
      'toggleFreeBetModal',
      'toggleFreeBetExpiredModal',
      'showTerminalNotification',
      'setActiveTerminalLayout',
      'setPrintedTicketByTicketCheck',
      'removePrintedTicketByTicketCheck',
      'disableAllBetting',
      'setUserData',
    ]),
    ...mapActions('user', ['getUserBalance', 'setUserFreeBets', 'getUserFreeBets', 'updateUser']),
    ...mapActions('notifications', ['setNotification', 'clearNotification']),
    ...mapActions('gamesBetslip', [
      'setIsPayinButtonDisabled',
      'setIsPayinInProgress',
      'cancelTicket',
      'clearBetslip',
      'getLastTickets',
      'setScheduleDisabled',
      'setIsPayinButtonDisabled',
      'payIn',
      'ticketCheck',
    ]),
    async clearAndSetNotification(notification) {
      await this.clearNotification();
      this.setNotification(notification);
    },
    setGtag(device) {
      if (this.config.ui.config.gtm) {
        const { code, auth, preview } = this.config.ui.config.gtm[device];
        gtag.setup(code, auth, preview);
        return;
      }
      gtag.setup();
    },
    closeCancelTicketModal() {
      this.ticketCanceled = false;
    },
    formatTicketBets() {
      if (this.isTerminalChannel) {
        return this.ticket.map((n) => ({
          payin: Number(n.stake) || Number(this.totalPayment),
          stake: n.stake,
          type: n.betTypeId,
          value: n.racers,
          displayValue: n.displayValue,
          event: n.event,
          numOfComb: n.numOfComb,
          id: n.betTypeId,
          input: n.input,
          displayName: n.displayName,
          numEvents: 1,
          eventId: n.eventId,
        }));
      }
      return this.ticket.map((n) => ({
        payin: Number(n.stake) || Number(this.totalPayment),
        stake: n.stake,
        type: n.betTypeId,
        value: n.racers,
        numEvents: 1,
        eventId: n.eventId,
      }));
    },
    setTheme(config) {
      const rootElement = document.documentElement;
      const baseColor = config.ui[config.ui.config.baseColor];
      const theme = config.ui[config.ui.config.theme];
      applyPalette.set(merge(theme, baseColor), rootElement);
    },
    setUser(user) {
      if (user.user) {
        this.isLoggedIn = !!user.token || !!user?.id;
        this.setUserData(user.user);
        if (this.isLoggedIn) {
          this.setUserUuid(user.user.id);
          this.togglePlayerChannel(this.userProfile.profile.id);
          this.updateUser({
            auth: {
              token: user.token,
              tpToken: user.tpToken,
            },
            profile: user.user,
          });
          if (window.WebAppListener) {
            this.getUserFreeBets();
          }
          if (!this.lastTicketsCalled) {
            this.getLastTickets();
          }
          this.lastTicketsCalled = !!user.token;
        }
      }
    },
    clearCheckTimeout() {
      clearTimeout(this.check);
    },
    checkTicket(requestUuid, action) {
      this.check = setTimeout(async () => {
        try {
          const response = await this.ticketCheck(requestUuid);
          const { data } = response;
          if (data.status.value.toUpperCase() !== 'PENDING') {
            if (this.isTerminalChannel) {
              const actionMessages = {
                Add: this.translations.ticket_add_success,
                Cancel: this.translations.ticket_cancel_success
                  .replace(
                    '{amount}',
                    data.cancelledAmount?.toFixed(2) ?? data.stake?.toFixed(2) ?? data.payin?.toFixed(2),
                  )
                  .replace('{currency}', this.config.currency),
                Payout: this.translations.ticket_payout_success
                  .replace('{amount}', data.payout?.toFixed(2))
                  .replace('{currency}', this.config.currency),
              };

              const message = actionMessages[action] || '';

              // If ticket details are open, show ticket after action is complete
              if (this.ticketDetailsData) this.setTicketDetailsData(data);

              this.showTerminalNotification({
                message,
                type: 'success',
                delay: 3000,
              });

              const newTicket = cloneDeep(data);
              newTicket.action = action;

              this.busService.sendMessage(
                {
                  action: 'Tickets.Update',
                  enforceEvent: true,
                  data: {
                    action,
                    ticketData: newTicket,
                    showMessage: false,
                  },
                },
                true,
              );

              if (action === 'Cancel' && (data.status.value !== 'CLOSED' || !this.isThirdPartyIntegration)) return;
              if (
                action === 'Payout' &&
                data.status.value !== 'PAYEDOUT' &&
                (!this.isThirdPartyIntegration || !this.userIsOperator)
              )
                return;
              if (
                // eslint-disable-next-line max-len
                this.printedTicketsByTicketCheck.some(
                  (printedTicket) =>
                    printedTicket.requestUuid === data.requestUuid && printedTicket.status.value === data.status.value,
                )
              )
                return;

              this.setPrintedTicketByTicketCheck(newTicket);
              this.printTicket(newTicket);
            } else {
              const message = {
                message: this.translations.general_ticket_confirmed,
                status: 'positive',
                notificationTimeout: 3000,
              };
              this.clearAndSetNotification(message);
              this.getLastTickets();
            }
            if (this.disabledRoundBySocket) {
              this.stopBetting(this.disabledRoundBySocket);
            } else {
              this.stopBetting(0);
            }
            this.setScheduleDisabled(false);
            this.clearBetslip();
          }
          this.clearCheckTimeout();
        } catch (error) {
          const message = error.response?.data?.message ?? error.message;
          if (this.isTerminal) {
            this.busService.sendMessage(
              {
                action: 'Tickets.Update',
                enforceEvent: true,
                data: {
                  action: 'Add',
                  ticketData: {
                    product: this.config.productAlias,
                    requestUuid,
                  },
                  showMessage: false,
                },
              },
              true,
            );

            this.showTerminalNotification({
              message,
              type: 'warning',
              delay: 3000,
            });
          } else {
            this.clearAndSetNotification({
              message,
              type: 'warning',
              notificationTimeout: 3000,
            });
          }
        } finally {
          this.setIsPayinButtonDisabled(false);
          this.setIsPayinInProgress(false);
        }
      }, this.checkTimeout);
    },
    printTicketIfNotPrinted(ticket) {
      // In case ticket update is late, check if ticket has already been printed
      if (
        // eslint-disable-next-line max-len
        this.printedTicketsByTicketCheck.some(
          (printedTicket) => printedTicket.id === ticket.id && printedTicket.status.value === ticket.status.value,
        )
      ) {
        this.removePrintedTicketByTicketCheck(ticket.id);
      } else {
        this.printTicket(ticket);
      }
    },
    printTicket(ticket) {
      const actionTypes = {
        add: 'TicketPayIn',
        payout: 'TicketPayout',
        cancel: 'TicketCancel',
      };

      const layoutNames = {
        add: `ticket${this.config.productAlias}`,
        payout: `ticket${this.config.productAlias}Payout`,
        cancel: 'ticketCancel',
      };

      const isFuture = this.checkIsTicketFuture(ticket);
      ticket.isFuture = isFuture;

      this.busService.sendMessage(
        {
          action: 'Peripherals.Print',
          enforceEvent: true,
          data: {
            layoutName: layoutNames[toLower(ticket.action)],
            productId: this.config.productAlias,
            data: ticket,
            additionalData: {
              clientPrintJobIdentifier: {
                id: actionTypes[toLower(ticket.action)],
                uuid: ticket.requestUuid,
              },
            },
          },
        },
        true,
      );
    },
    getPrintTemplate() {
      let template = '';
      const messages = this.translations;
      const { currency } = this.config;
      const tempTickets = JSON.parse(JSON.stringify(this.ticket));
      const printBets = merge(tempTickets, this.formatTicketBets());
      template += `${this.getProductName};`;
      template += "{{ ticketDateTimeUTC | date('d.m.Y H:i:s', false) }};";
      template += `${messages.general_pay_in} {{ payin | number_format(2, '.', ',') }}${currency} `;
      template += `${messages.general_payout} {{ payout | number_format(2, '.', ',') }}${currency} `;

      return (
        template +
        printBets
          .map((bet) => {
            const betOutcome = isArray(bet.racers) ? bet.racers.join(',') : bet.racers;
            let body = '';

            for (let i = 0; i < bet.numEvents; i += 1) {
              body += `${betOutcome} `;
              body += `${bet.market} `;
              body += `${bet.round} `;
              body += `${bet.odds === null ? null : bet.odds.toFixed(2)} `;
              body += `${bet.payin} `;
            }
            return body;
          })
          .join('; ') +
        '{{ id }}'
      );
    },
    setPageTitle() {
      const { productFqn, applicationName } = this.config;
      document.title = `${startCase(productFqn)}: ${applicationName}`;
    },
    togglePlayerChannel(playerId) {
      if (playerId) {
        this.busService.addChannel(`${this.config.productName}.Player`, playerId);
      } else {
        this.busService.removeChannel(`${this.config.productName}.Player`, this.userProfile.profile.id);
      }
    },
    claimFreeBets() {
      this.clearBetslip();
      this.setUserFreeBets(this.userFreeBets);
      this.toggleFreeBetModal(false);
    },
    resetUserFreeBets() {
      this.userFreeBets = {
        count: 0,
        betStake: 0,
      };
    },
    async ticketPayin() {
      this.setScheduleDisabled(true);
      this.setIsPayinInProgress(true);
      this.stopBetting(this.currentRound);
      this.setIsPayinButtonDisabled(true);
      try {
        const response = await this.payIn({
          additionalInfo: this.isIsonis
            ? {
                printTemplate: this.getPrintTemplate(),
                timezone: {
                  offset: parseInt(this.userProfile.profile.profile.timezoneOffset, 10),
                },
              }
            : null,
          payload: this.formatTicketBets,
          providerBetslipId: ulid() || null,
        });
        // IGNITE IMPLEMENTATION
        if (this.isNextGen && response.status === 201) {
          this.setIsPayinInProgress(false);
          this.setIsPayinButtonDisabled(false);
          this.setScheduleDisabled(false);
          this.stopBetting(0);

          const message = {
            message: this.translations.general_ticket_confirmed,
            status: 'positive',
            notificationTimeout: 3000,
          };
          await this.clearBetslip();
          this.clearAndSetNotification(message);
          this.getLastTickets();
          this.getUserFreeBets();
          if (this.nextgenStandaloneIntegration) {
            await window.igniteInstance.getWallets().then((result) => {
              if (result.activeCurrencyBalance) {
                this.setUserBalance(result.activeCurrencyBalance);
              }
            });
          }
        } else {
          // seven
          await this.clearBetslip();
          this.checkTicket(response.data.requestUuid);
          const message = {
            message: this.translations.general_ticket_resolving,
            status: 'neutral',
          };
          this.setNotification(message);
        }
      } catch (error) {
        if (error) {
          const message = {
            message: this.translations.general_ticket_error,
            status: 'negative',
            notificationTimeout: 3000,
          };
          this.setNotification(message);
          this.setIsPayinInProgress(false);
          this.setScheduleDisabled(false);
          this.stopBetting(0);
          this.setIsPayinButtonDisabled(false);
        }
        if (error.response) {
          const errorData = error.response.data;
          const message = {
            message: errorData.message,
            status: 'negative',
          };
          this.setIsPayinInProgress(false);
          this.setNotification(message);
        }
      }
    },
    async ticketPayinTerminal() {
      if (this.isThirdPartyIntegration) {
        this.showTerminalNotification({
          message: this.translations.general_ticket_resolving,
          type: 'info',
          delay: null,
        });
      }
      const additionalInfo = {
        printTemplate: this.getPrintTemplate(),
        timezone: {
          offset: 1,
        },
      };
      try {
        const response = await this.payIn({
          payload: this.formatTicketBets,
          additionalInfo: this.isIsonis ? additionalInfo : null,
        });
        const config = {
          ticketGroup: this.config.productName,
        };
        const formatedPayload = response;
        formatedPayload.ticket.stake = response.ticket.payin;
        formatedPayload.ticket.config = config;
        if (formatedPayload.ticket.payin > this['user/user'].balance) {
          this.showTerminalNotification({
            message: this.translations.player_balance_insufficient,
            type: 'warning',
            delay: 3000,
          });
          return;
        }
        this.setScheduleDisabled(true);
        this.setIsPayinInProgress(true);
        this.stopBetting(this.currentRound);
        this.setIsPayinButtonDisabled(true);
        this.busService.clientAdapter.handler
          .emitAsync({
            action: 'Tickets.Pay',
            data: formatedPayload,
            async: true,
          })
          .then((response2) => {
            this.checkTicket(response2.ticket.requestUuid, 'Add');
          });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error.message);
      }
    },
    handleSetState(eventDetail) {
      const { eventId, offer, type, odds } = eventDetail;
      if (eventId) {
        this.setCurrentScheduleRound(eventId);

        if (this.userLogged) {
          this.getUserFreeBets();
        }
      }
      if (this.userLogged) {
        this.getLastTickets();
      }
      this.setState(eventDetail);
      if (eventId) {
        this.setInProgressRound(eventId);
        this.setNextRoundTime(offer[0].startDate.date);
      }
      if (!this.isSetStateTransfered && eventId && type) {
        this.setStateMessage(event.detail);
      }
      if (type === 'race' && eventId === offer[0].eventIdToday) {
        this.stopBetting(eventId);
        setTimeout(() => {
          eventBus.$emit('switchToNextRound');
        }, 1000);
      }
      if (type === 'bettingDisabled') {
        this.stopBetting(eventId);
        setTimeout(() => {
          eventBus.$emit('switchToNextRound');
        }, 1000);
      }
      if (offer) {
        this.setOffer(offer);
        this.parseOdds(odds);
        this.updateTicketValues();
        eventBus.$emit('updateSchedule');
      }
      if (type === 'race') {
        this.parseOdds(offer[0].odds);
      }
      if (this.config?.state?.offer) {
        this.setOffer(this.config.state.offer);
      }
      this.setLoaded(true);
    },
    async handleStartEvent(eventDetail) {
      const { eventId, offer } = eventDetail;
      if (this.isTerminalChannel && this.ticketDetailsData) {
        if (this.ticketDetailsData.status && this.ticketDetailsData.status.id !== '5') {
          const path = this.config.platformDataCenter
            ? `VUE_APP_TERMINAL_TICKET_CHECK_PATH_${toUpper(this.config.platformDataCenter)}`
            : `VUE_APP_TERMINAL_TICKET_CHECK_PATH`;
          fetch(`${process.env[path]}/${this.ticketDetailsData.id}.json`, {
            headers: {
              Authorization: `Basic ${this.getTicketDetailsAuth}`,
              'SEVEN-LOCALE': this.config.locale,
            },
          })
            .then((response) => response.text())
            .then((text) => this.setTicketDetailsData(JSON.parse(text)));
        }
      }
      this.setCurrentScheduleRound(eventId);
      this.setNextRoundTime(offer[0].startDate.date);
      this.clearNotification();
      this.setState(eventDetail);
      this.stopBetting(0);
      this.setOffer(offer);
      this.parseOdds(this.config.state.odds);
      eventBus.$emit('updateSchedule');
      if (this.userLogged) {
        this.getLastTickets();
        if (this.nextgenStandaloneIntegration) {
          await window.igniteInstance.getWallets().then((result) => {
            if (result.activeCurrencyBalance) {
              this.setUserBalance(result.activeCurrencyBalance);
            }
          });
        }
        if (this.isStandAlone && !this.isGravityGateway) {
          this.getUserBalance();
        }
      }
    },
    handleStopBetting(eventDetail) {
      const { eventId } = eventDetail;
      this.stopBetting(eventId);
      this.setDisabledRoundBySocket(eventId);
      this.updateTicketValues();
      if (this.expiredBets) {
        this.clearAndSetNotification({
          message: this.translations.general_invalid_bets_accept,
          status: 'neutral',
          type: this.notificationTypes.INVALID_BETS,
        });
      }
    },
    handleStartRace(eventDetail) {
      const { eventId } = eventDetail;
      this.setInProgressRound(eventId);
      this.offer.shift();
      eventBus.$emit('updateSchedule');
      // Fix edge case for visualization started during betting disabled time
      // and not getting bounced message for starting the race (CU-8694ghrvg)
      if (!this.isSetStateTransfered) {
        this.setStateMessage(eventDetail);
      }
      if (this.userLogged) {
        setTimeout(() => {
          this.getLastTickets();
        }, 500);
      }
    },
    handleAuthorizationChanged(eventDetail) {
      const { auth } = eventDetail.data;
      if (!this.isTerminalChannel) {
        const authData = auth;
        if (authData) {
          const playerId = authData?.user?.id || null;
          this.togglePlayerChannel(playerId);
          this.setUser(authData);
        }
        if (this.userLogged) {
          this.setUserBalance(auth.user.balance);
          this.getUserFreeBets();
        } else {
          this.setIsPayinButtonDisabled(false);
          this.busService.removeChannel(`${this.config.productName}.Player`, this.userUuid);
          this.lastTicketsCalled = false;
          this.clearBetslip();
          eventBus.$emit('closeTicketHistory');
        }
        if (this.isStandAlone && this.isGravityGateway) {
          this.getUserBalance();
        }
        if (this.isNextGen) {
          this.updateUser({
            auth: eventDetail.data?.auth,
            profile: eventDetail.data?.auth?.user,
          });
        }
      }
    },
    handleWebTicketUpdate(eventDetail) {
      if (!this.isUserLoggedIn) return;
      const { id, status, code } = eventDetail;
      const message = {
        message: this.translations.general_ticket_confirmed,
        status: 'positive',
        notificationTimeout: 3000,
      };
      if (id && status.id === '0') {
        this.busService.sendMessage(
          {
            action: 'Tickets.Resolved',
            data: {
              accepted: true,
              ticket: eventDetail,
            },
          },
          true,
        );
        if (this.isAndroidPlatform) {
          window.WebAppListener.sendMessage(
            JSON.stringify({
              action: 'Tickets.Resolved',
              data: {
                accepted: true,
                ticket: eventDetail,
              },
            }),
          );
        }
        this.getUserFreeBets();
      }
      if (id && status.id === '1') {
        message.message = this.translations.general_ticket_canceled;
        message.status = 'neutral';
        this.canceledTicket = eventDetail;
        this.ticketCanceled = true;

        eventBus.$emit('RefreshTicketHistory');
      }
      if (code) {
        message.message = eventDetail.message;
        message.status = 'negative';
        this.clearAndSetNotification(message);
      } else {
        (async () => {
          await this.clearBetslip();
          this.clearAndSetNotification(message);
        })();
      }
      this.getLastTickets();
      this.setIsPayinButtonDisabled(false);
      this.setIsPayinInProgress(false);
      this.clearCheckTimeout();
      if (this.disabledRoundBySocket) {
        this.stopBetting(this.disabledRoundBySocket);
      } else {
        this.stopBetting(0);
      }
      this.setScheduleDisabled(false);
      if (this.isStandAlone && !this.isGravityGateway) {
        this.getUserBalance();
      }
      eventBus.$emit('SwitchToBetslipTab');
    },
    handleTerminalTicketUpdate(eventDetail) {
      const { status, code, ticketPin, payout, cancelledAmount } = eventDetail;
      const ticket = JSON.parse(JSON.stringify(eventDetail));
      ticket.ticketPin = ticketPin;
      ticket.bets?.forEach((bet) => {
        // eslint-disable-next-line no-param-reassign
        bet.possibleTax = bet.possibleTax || 0;
      });
      if (status.id === '0' && this.activeTerminalLayout === 'BettingArea') {
        ticket.translation = this.productNameLabel;
        ticket.payinConfirmation = this.translations['print.payin_confirmation'];
        this.printTicketIfNotPrinted(ticket);
        this.busService.sendMessage(
          {
            action: 'Tickets.Update',
            enforceEvent: true,
            data: {
              action: 'Add',
              ticketData: ticket,
              showMessage: false,
            },
          },
          true,
        );
        this.showTerminalNotification({
          message: this.translations['ticket.add_success'],
          type: 'success',
          delay: 3000,
        });
      }
      if (status.id === '5' && this.activeTerminalLayout === 'TicketCheck') {
        this.busService.sendMessage(
          {
            action: 'Tickets.Update',
            enforceEvent: true,
            data: {
              action: 'Payout',
              ticketData: ticket,
              showMessage: false,
            },
          },
          true,
        );
        if (this.userIsOperator) {
          const isFuture = this.checkIsTicketFuture(ticket);
          ticket.isFuture = isFuture;
          this.busService.sendMessage(
            {
              action: 'Peripherals.Print',
              enforceEvent: true,
              data: {
                layoutName: `ticket${this.config.productAlias}Payout`,
                productId: this.config.productAlias,
                data: ticket,
              },
            },
            true,
          );
        }
        this.showTerminalNotification({
          message: this.translations['ticket.payout_success_to_balance'].replace('{{amount}}', payout.toFixed(2)),
          type: 'success',
          delay: 3000,
        });
        const path = this.config.platformDataCenter
          ? `VUE_APP_TERMINAL_TICKET_CHECK_PATH_${toUpper(this.config.platformDataCenter)}`
          : `VUE_APP_TERMINAL_TICKET_CHECK_PATH`;
        fetch(`${process.env[path]}/${this.ticketDetailsData.id}.json`, {
          headers: {
            Authorization: `Basic ${this.getTicketDetailsAuth}`,
            'SEVEN-LOCALE': this.config.locale,
          },
        })
          .then((response) => response.text())
          .then((text) => this.setTicketDetailsData(JSON.parse(text)));
      }
      if (status.id === '1' && this.activeTerminalLayout === 'TicketCheck') {
        this.busService.sendMessage(
          {
            action: 'Tickets.Update',
            enforceEvent: true,
            data: {
              action: 'Cancel',
              ticketData: ticket,
              showMessage: false,
            },
          },
          true,
        );
        this.showTerminalNotification({
          message: this.translations['ticket.cancel_success_to_balance'].replace(
            '{{amount}}',
            cancelledAmount.toFixed(2),
          ),
          type: 'success',
          delay: 3000,
        });
        this.setTicketDetailsData(ticket);
      }
      if (code) {
        this.showTerminalNotification({
          message: eventDetail.message,
          type: 'warning',
          delay: 3000,
        });
        this.busService.sendMessage(
          {
            action: 'Tickets.Update',
            enforceEvent: true,
            data: {
              action: 'Payout',
              ticketData: ticket,
              showMessage: false,
            },
          },
          true,
        );
        this.setIsPayinInProgress(false);
      }
      if (status.id === '12') {
        this.busService.sendMessage(
          {
            action: 'Tickets.Update',
            enforceEvent: true,
            data: {
              action: 'Add',
              ticketData: ticket,
              showMessage: false,
            },
          },
          true,
        );
      }
      this.stopBetting(0);
      this.setScheduleDisabled(false);
      this.setIsPayinButtonDisabled(false);
      this.clearCheckTimeout();
      this.clearBetslip();
      this.setIsPayinInProgress(false);
    },
    handleTicketsFetchHistory() {
      const path = this.config.platformDataCenter
        ? `VUE_APP_TERMINAL_TICKET_HISTORY_PATH_${toUpper(this.config.platformDataCenter)}`
        : `VUE_APP_TERMINAL_TICKET_HISTORY_PATH`;
      fetch(
        `${process.env[path]}/${this.config.productId}.json?deviceUuid=${this.config.device.deviceUuid}&timeFrom=${event.detail.data.timeFrom}&timeTo=${event.detail.data.timeTo}&count=${event.detail.data.count}`,
        {
          headers: this.getTicketHistoryAuthHeaders,
        },
      )
        .then((response) => response.text())
        .then((text) => this.setTicketHistoryTerminal(JSON.parse(text)));
    },
    handleTicketsChecked(eventDetail) {
      const { ticket } = eventDetail.data;
      let ticketPin = null;
      if (ticket.ticketPin) {
        ticketPin = ticket.ticketPin;
      }
      this.setLoaded(false);
      const path = this.config.platformDataCenter
        ? `VUE_APP_TERMINAL_TICKET_CHECK_PATH_${toUpper(this.config.platformDataCenter)}`
        : `VUE_APP_TERMINAL_TICKET_CHECK_PATH`;
      fetch(`${process.env[path]}/${ticket.id}.json`, {
        headers: {
          Authorization: `Basic ${this.getTicketDetailsAuth}`,
          'SEVEN-LOCALE': this.config.locale,
        },
      })
        .then((response) => response.text())
        .then((text) => {
          const formated = JSON.parse(text);
          formated.ticketPin = ticketPin;
          this.setTicketDetailsData(formated);
        });
    },
    checkIsTicketFuture(ticket) {
      const { bets } = ticket;
      let isFuture = false;
      if (!bets || bets.length === 1) return isFuture;
      const firstBetRound = bets[0].eventId;
      bets.forEach((bet) => {
        if (firstBetRound !== bet.eventId) isFuture = true;
      });
      return isFuture;
    },
  },
};
</script>

<style lang="scss">
@use './assets/style/helpers/_modal.scss';

* {
  box-sizing: border-box;
}

html,
body {
  padding: 0;
  margin: 0;
  width: 100%;
  overflow: hidden;
  scrollbar-width: none;
  -webkit-tap-highlight-color: transparent;
  ::-webkit-scrollbar {
    width: 0 !important;
  }
  -webkit-text-size-adjust: 100%; /* Prevent font scaling */
  text-size-adjust: 100%;
}

#app {
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 100%;
  height: 100vh;
  &.hide-cursor {
    * {
      cursor: none !important;
    }
  }
}
.app {
  background-repeat: no-repeat;
  background-size: cover;
  height: 100vh;
  .cancel-ticket-confirm-modal,
  .free-bet-expired-modal {
    .modal {
      height: 182px;
    }
  }

  .cancel-ticket-confirm-modal,
  .free-bet-modal,
  .free-bet-expired-modal {
    .modal {
      background-color: var(--card);
      height: 182px;

      .modal-header,
      .modal-body {
        color: var(--text-primary-1);
      }

      .modal-header {
        font-size: 16px;
        padding: 0 16px !important;
      }
      .modal-body {
        font-size: 14px;
        .modal-actions {
          display: none;
        }
      }
      .modal-footer {
        padding-right: 43px;
      }
      .button {
        background: transparent;
        border: 0;
        outline: 0;
        font-size: 14px;
        color: #ffc107;
        text-transform: uppercase;
        cursor: pointer;
        font-family: Roboto, 'sans-serif';
        padding: 31px 0 27px 0;
      }
    }
  }
}
::v-deep .ticket-details-body-desktop {
  .rows {
    overflow: hidden !important;
  }
}
.modal-back.cancel-ticket {
  .modal.show-modal {
    .modal-header {
      h3 {
        margin: 0 !important;
        font-size: 18px !important;
        text-align: left;
      }
    }
    .modal-body {
      text-align: left;
    }
    .modal-footer {
      .button {
        margin-left: 60px;
      }
    }
  }
  .modal.popup-show-modal {
    padding: 24px !important;
    .modal-header {
      padding-left: 0px !important;
      h3 {
        margin: 0 !important;
        font-size: 18px !important;
      }
    }
  }
}

.cancel-ticket-wrapper-mobile {
  .modal-back.cancel-ticket {
    .modal-header {
      margin: 0 !important;
      padding-top: 0 !important;
    }
    .modal-body {
      padding: 0 !important;
    }
    .modal-footer {
      padding: 0 !important;
    }
  }
}
.free-bet-modal {
  .modal {
    max-height: 348px !important;
    .modal-header {
      display: none;
    }
    .modal-body {
      height: 348px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      background-position: 0 0;
      background-repeat: no-repeat;
      position: relative;
      text-align: center;
      .body {
        height: 100%;
        img {
          position: absolute;
          top: -2%;
          left: 24px;
          max-width: 100%;
        }
        .body-message-box {
          position: absolute;
          width: 100%;
          top: 66%;
          left: 50%;
          transform: translate(-50%, -50%);

          .title {
            font-size: 42px;
            font-weight: 700;
            color: #90b653;
            margin-bottom: 25px;
          }
          .subtitle {
            font-size: 18px;
            font-weight: 700;
          }
          .subtitle-2 {
            font-size: 14px;
            color: var(--text-primary-3);
          }
        }
      }
    }
    .modal-footer {
      display: none;
    }
  }
  &.desktop {
    .modal {
      .modal-body {
        background-image: none;
        .body {
          img {
            height: 60%;
            left: 50%;
            top: -2%;
            transform: translate(-50%, 0);
          }
          .body-message-box {
            top: 60%;
            .title {
              margin-bottom: 10px;
            }
            .pre-title {
              display: none;
            }
          }
          .action {
            position: absolute;
            bottom: 0;
            right: 16px;
          }
        }
      }
    }
  }
  &.mobile {
    .modal {
      .modal-body {
        background-image: url('assets/free-bet-bg.svg');
        .body {
          .body-message-box {
            .title {
              text-transform: uppercase;
            }
          }
          .action {
            position: absolute;
            left: 0;
            bottom: 20px;
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: center;
            .button {
              min-width: 150px;
              background: #90b653;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 30px;
              padding: 0;
              color: var(--text-secondary-1);
              font-weight: 500;
            }
          }
        }
      }
    }
  }
}
@media all and (max-width: 420px) {
  .modal-back.cancel-ticket {
    padding: 0px;
    .modal.show-modal {
      .modal-header {
        h3 {
          margin: 0 !important;
          font-size: 18px !important;
        }
      }
    }
    .modal.popup-show-modal {
      .modal-header {
        padding-left: 0px !important;
        padding-bottom: 0px !important;
        padding-top: 0px !important;
        font-size: 18px;
        font-weight: 700;
        h3 {
          margin: 0 !important;
          font-size: 18px !important;
        }
      }
      .modal-body {
        font-size: 16px !important;
        padding: 15px 0px 0px 0px !important;
      }
      .modal-footer {
        padding: 0px 0px 24px 30px !important;
        .button {
          margin-left: 60px;
        }
      }
    }
  }
}
@media (max-width: 640px) {
  .app {
    .cancel-ticket-confirm-modal {
      background-color: var(--overlay-lock);
      .modal {
        background-color: var(--card);
        color: var(--text-primary-1);
        height: 182px !important;

        .modal-header {
          background-color: var(--card);
          padding: 0 24px 16px 24px;
        }

        .modal-body {
          position: relative;
          padding: 0 24px;
          color: var(--text-primary-1);
          .modal-actions {
            display: block;
            position: absolute;
            bottom: 0;
            right: 0;
            padding-right: 43px;
          }
        }

        .modal-footer {
          display: none;
        }
      }
    }
    .free-bet-modal {
      .modal {
        max-height: 70% !important;
        .modal-body {
          height: 100% !important;
        }
      }
    }
  }
}
</style>
